import { Copy, FileText, Loader2, Merge, PenLine, PhoneIcon } from "lucide-react";

import { APP_ICON_MAP } from "@/v2/lib/appIcons";
import { Badge } from "@/v2/components/ui/badge";
import { Checkbox } from "@/v2/components/ui/checkbox";
import type { Entry } from "@/shared/types/entries";
import { formatTime } from "@/features/attorney/components/EntriesTable/EntriesTable";
import { HighlightedNarrative } from "@/v2/components/HighlightedNarrative";
import { cn } from "@/v2/lib/utils";
import { useLongPress } from "@/v2/hooks/useLongPress";

export function AppIcon({ application }: { application?: string }) {
	if (application === "Phone") return <PhoneIcon className="size-11 text-muted-foreground" />;
	if (application === "User Input") return <PenLine className="size-11 text-muted-foreground" />;
	if (application === "Combine") return <Merge className="size-11 text-muted-foreground" />;
	if (application === "Duplicate") return <Copy className="size-11 text-muted-foreground" />;

	const icon = application && APP_ICON_MAP[application];
	if (icon) return <img src={icon} alt={application} className="size-11" />;

	return <FileText className="size-11 text-muted-foreground" />;
}

interface EntryCardProps {
	entry: Entry;
	onClick?: (entry: Entry) => void;
	selectionMode?: boolean;
	selected?: boolean;
	onIconClick?: (entry: Entry, e: React.MouseEvent) => void;
	onRowSelect?: (entry: Entry, e: React.MouseEvent) => void;
	onLongPress?: (entry: Entry) => void;
	exportFailed?: boolean;
	isMutating?: boolean;
}

export function EntryCard({ entry, onClick, selectionMode, selected, onIconClick, onRowSelect, onLongPress, exportFailed, isMutating }: EntryCardProps) {
	const hasIssue = entry.needs_improvement || entry.attorney_feedback;

	const longPress = useLongPress(
		() => onLongPress?.(entry),
		{ delay: 500, moveThreshold: 10, feedbackDelay: 200 },
	);

	const handleIconClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onIconClick?.(entry, e);
	};

	const handleRowClick = (e: React.MouseEvent) => {
		if (selectionMode) {
			// In selection mode, taps always toggle (like ctrl+click)
			onRowSelect?.(entry, { ...e, ctrlKey: true, metaKey: true } as React.MouseEvent);
		} else {
			onClick?.(entry);
		}
	};

	return (
		<div
			onClick={handleRowClick}
			{...(onLongPress && !selectionMode ? {
				onTouchStart: longPress.onTouchStart,
				onTouchMove: longPress.onTouchMove,
				onTouchEnd: longPress.onTouchEnd,
				onTouchCancel: longPress.onTouchCancel,
				onContextMenu: longPress.onContextMenu,
			} : {})}
			className={cn(
				"relative w-full px-4 py-3 text-left border-b border-border transition-all cursor-pointer",
				exportFailed
					? "border-l-2 border-l-destructive"
					: hasIssue
						? "border-l-2 border-l-warning"
						: undefined,
				isMutating && "opacity-50 pointer-events-none cursor-not-allowed",
				selected
					? "bg-primary/10"
					: "active:bg-secondary/60 hover:bg-secondary/40",
				selectionMode && "select-none",
			)}
		>
			{/* Icon — vertically centered with matter block */}
			<div className="flex items-start gap-3">
				<div
					onClick={handleIconClick}
					className="shrink-0 flex items-center justify-center w-8 h-8 mt-0.5"
				>
					{selectionMode ? (
						<Checkbox
							checked={selected}
							onCheckedChange={() => {
								onIconClick?.(entry, { ctrlKey: true, metaKey: true, shiftKey: false } as React.MouseEvent);
							}}
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						<AppIcon application={entry.application} />
					)}
				</div>

				{/* All content to the right of icon */}
				<div className="flex-1 min-w-0">
					{/* Matter block + time/duration */}
					<div className="flex items-start justify-between gap-2">
						<div className="min-w-0 -my-0.5">
							<p className="truncate text-sm font-medium leading-tight">
								{entry.matter || <span className="text-muted-foreground italic">No matter</span>}
							</p>
							{(entry.matter_name || entry.client) && (
								<p className="truncate text-xs text-muted-foreground leading-tight">
									{[entry.matter_name, entry.client].filter(Boolean).join(" · ")}
								</p>
							)}
							{entry.end_time && (
								<p className="text-[0.6875rem] text-muted-foreground/70 leading-tight">
									{formatTime(entry.end_time)}
								</p>
							)}
						</div>
						<span className="text-xs font-medium text-foreground/70 shrink-0">
							{entry.duration.toFixed(2)}h
						</span>
					</div>

					{/* Narrative */}
					{entry.description ? (
						<HighlightedNarrative
							description={entry.description}
							matterId={entry.matter_id}
							client={entry.client}
							className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap"
						/>
					) : (
						<p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
							<span className="italic">No narrative</span>
						</p>
					)}

					{/* Task / Activity codes */}
					{(entry.task || entry.activity) && (
						<div className="flex items-center gap-1.5 mt-2">
							{entry.task && <Badge variant="muted">{entry.task}</Badge>}
							{entry.activity && <Badge variant="muted">{entry.activity}</Badge>}
						</div>
					)}
				</div>
			</div>

			{/* Mutating spinner */}
			{isMutating && (
				<Loader2 className="absolute bottom-2.5 right-3 size-3 animate-spin text-muted-foreground" />
			)}
		</div>
	);
}
